import { formatEnumString } from "../../pipes/pipe-enum-utils";
import AddressType from "../address-type";
import EmployeeDetail from "../company-resources/employee-detail";
import LocationDto from "../company-resources/location-dto";
import Address from "../contact/address";
import BusinessPhoneNumber from "../contact/business-phone-number";
import Gender from "../gender";
import Credential from "./credential";
import CredentialType from "./credential-type.enum";

export default class CredentialedResource {
  groupAffiliation: string;
  userId: string;
  accountName: string;

  resourceType: string = "4";
  id: number;
  tenantId: number;
  firstName: string;
  lastName: string;
  prefix: string;
  suffix: string;
  emailAddress: string;
  addresses: Address[];
  preferredName: string;
  middleInitial: string;
  notes: string;
  gender: Gender;
  dateOfBirth: Date;
  primaryLanguage: string;
  phoneNumbers: BusinessPhoneNumber[];
  credentials: Credential[];
  externalId: number;
  isEmployee: boolean;
  _resourceStatus: number = 1;
  _status: string;
  employeeInformation: EmployeeDetail;
  professionalRelation: number;
  npi: string;
  employedBy: string;
  effectiveDate: string;
  deaStartDate: string;
  deaExpireDate: string;
  contactLocations?: LocationDto[];
  location?: LocationDto;
  otherLocations?: LocationDto[];
  cognitoUserId?: number;
  companyId?: string;
  companyName?;
  string;
  defaultAddress? = new Address();
  isSystemUser?: boolean = false;
  /**
   * OPS-2858. Discriminates internal support users, who hold access to multiple
   * companies. Must ROUND-TRIP on GET and PUT: the server maps it straight onto
   * contacts.is_support_user, so a save that omits it drops the user out of the
   * Support Users tab.
   */
  isSupportUser?: boolean = false;
  workPhoneNumbers?: BusinessPhoneNumber[];
  faxPhoneNumbers?: BusinessPhoneNumber[];
  cellPhoneNumbers?: BusinessPhoneNumber[];

  constructor() {
    this.defaultAddress.id = 0;
    this.defaultAddress.addressType = AddressType.PHYSICAL;
  }

  get isAUser(): boolean {
    return !(!this.userId || !this.accountName);
  }

  get resourceStatus(): number {
    return this._resourceStatus;
  }

  set resourceStatus(statusId: number) {
    this._resourceStatus = statusId;
    if (statusId === 2) {
      this._status = "Inactive";
    } else {
      this._status = "Active";
    }
  }

  get status(): string {
    return this._status;
  }

  findAddressByType(type: number): Address {
    if (!this.addresses || this.addresses.length === 0) {
      return this.defaultAddress;
    }
    let address = this.addresses.find((x) => x.addressType === type);
    if (!address) {
      return this.defaultAddress;
    }
    return address;
  }

  getAddress() {
    if (!this.addresses || this.addresses.length === 0) {
      return this.defaultAddress;
    }
    let address = this.addresses.find(
      (x) => x.addressType === AddressType.PHYSICAL
    );
    if (!address) {
      address = this.addresses.find(
        (x) => x.addressType === AddressType.MAILING
      );
    }
    if (!address) {
      address = this.addresses.find(
        (x) => x.addressType === AddressType.OFFICE
      );
    }
    if (!address) {
      address = this.addresses.find((x) => x.addressType === AddressType.HOME);
    }
    if (!address) {
      return this.defaultAddress;
    }

    return address;
  }

  findCredential(): Credential {
    if (!this.credentials || this.credentials.length === 0) {
      return new Credential();
    } else {
      return this.credentials[0];
    }
  }

  get activeCredentials(): Credential[] {
    let credentials = [];
    this.credentials.forEach((x: Credential) => {
      if (x.active) {
        credentials.push(x);
      }
    });

    return credentials;
  }

  findPhoneNumberByType(type: number): BusinessPhoneNumber {
    if (!this.phoneNumbers) {
      return {
        phoneNumberType: type,
        phoneNumber: "",
        phoneNumberExtension: "",
        id: 0,
        deleted: false,
        primary: false,
      };
    }

    let pn = this.phoneNumbers.find((x) => x.phoneNumberType === type);
    if (!pn) {
      return {
        phoneNumberType: type,
        phoneNumber: "",
        phoneNumberExtension: "",
        id: 0,
        deleted: false,
        primary: false,
      };
    }

    return pn;
  }

  findDisplayName(date: Date = new Date()) {
    const filteredCredentials = this.credentials.filter(
      (credential) =>
        credential.operatingAs &&
        new Date(credential.validFromDatetime) <= date &&
        (date <= new Date(credential.validToDatetime) ||
          !credential.validToDatetime)
    );

    let credentials = "";
    if (filteredCredentials.length > 0) {
      credentials = filteredCredentials
        .map((credential) => formatEnumString(CredentialType[credential.type]))
        .join(", ");
      credentials = ` (${credentials})`;
    }
    return `${this.lastName}, ${this.firstName}${credentials}`;
  }

  getProviderId() {
    return this.externalId;
  }

  splitLocations() {
    if (!this.contactLocations && this.contactLocations.length < 1) {
      return false;
    }
    this.otherLocations = [];
    this.location = this.contactLocations.find((a) => a.isPrimaryLocation);
    this.otherLocations = this.contactLocations.filter(
      (a) => !a.isPrimaryLocation
    );

    return true;
  }
}



// WEBPACK FOOTER //
// ./src/models/resource-management/credentialed-resource.ts